import React from "react";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import RevealImage from "../components/RevealImage";

export default function About() {
  return (
    <>
      <Seo
        title="About"
        description="RM.NM Architects — a studio led by Rajanikant Machhar and Nishant Machhar, practicing from Bhavnagar since 1976."
      />

      {/* Header */}
      <section className="container-page pt-16 md:pt-24 pb-16 md:pb-24">
        <Reveal>
          <div className="eyebrow mb-6">About</div>
          <h1 className="font-display text-[2.25rem] sm:text-5xl md:text-8xl leading-[0.95] tracking-wider sm:tracking-widest2">
            Rajanikant Machhar<br />
            <span className="text-smoke">—</span> Nishant Machhar
          </h1>
        </Reveal>
      </section>

      {/* Philosophy */}
      <section className="container-page pb-24 md:pb-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-4">
            <div className="eyebrow">Practice</div>
          </Reveal>
          <Reveal className="md:col-span-8" delay={0.1}>
            <p className="font-display text-2xl md:text-4xl leading-snug tracking-wide">
              We are an architectural practice based at Bhavnagar, Gujarat. Established as a studio
              by Architect Rajanikant Machhar in 1976, we have evolved into an organization led by a
              core team of highly experienced design and engineering associates, simultaneously
              engaged in a diverse range of projects.
            </p>
            <div className="mt-10 space-y-6 text-base md:text-lg leading-relaxed text-smoke max-w-3xl">
              <p>
                Embracing a comprehensive design philosophy that is intuitive yet pragmatic, our
                practice is committed to architecture that reflects and celebrates the human spirit.
              </p>
              <p>
                We believe in practicing architecture that responds to the geographical and
                environmental context of a place, to human needs and aspirations, to the social and
                cultural context that defines a place, to space management and economics, and to
                the usage of appropriate technology — leading to built-form that befits a place and
                its purpose.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Rajanikant */}
      <section className="border-t border-mist">
        <div className="container-page py-24 md:py-40 grid grid-cols-1 md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-4">
            <RevealImage aspect="aspect-[4/5]" className="mb-8">
              <img
                src="/images/owner/Rajanikant_Machhar.jpg"
                alt="Rajanikant Machhar — Founder & Principal Architect"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </RevealImage>
            <div className="eyebrow mb-4">Founder — Principal Architect</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-6xl tracking-normal sm:tracking-widest2 leading-tight">
              Rajanikant<br />Machhar
            </h2>
          </Reveal>
          <Reveal className="md:col-span-8" delay={0.1}>
            <div className="space-y-6 text-base md:text-lg leading-relaxed">
              <p>
                Born in Bhavnagar, Gujarat, Rajanikant Machhar studied architecture at The Maharaja
                Sayaji Rao University (M. S. University), Baroda — graduating with a Bachelors
                degree in Architecture in 1975. He founded his practice in 1976 at Bhavnagar and
                has been leading the studio as the principal architect since.
              </p>
              <p className="text-smoke">
                He has been conferred with the prestigious <strong className="text-ink font-normal">JIIA Award for Excellence in Architecture — 1994, Mumbai</strong>
                {" "}for the Nirvan Tirth Electric Crematorium at Bhavnagar (Instituted by The
                Journal of Indian Institute of Architects), and the <strong className="text-ink font-normal">Architect of the Year Award — 1996, New Delhi</strong>
                {" "}with a Commendation Award for Religious Building for the same project
                (Instituted by The J K Cement Works).
              </p>
              <p className="text-smoke">
                He is a visiting juror at the Indubhai Parekh School of Architecture (IPSA) since
                2004 and has been an external thesis chair for a number of students. He continues
                to mentor interns and remains academically inclined.
              </p>
            </div>

            <dl className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-6 border-t border-mist pt-8">
              <div>
                <dt className="eyebrow">Education</dt>
                <dd className="mt-2 font-display text-xl tracking-wide">B. Arch · MSU Baroda · 1975</dd>
              </div>
              <div>
                <dt className="eyebrow">Practice Since</dt>
                <dd className="mt-2 font-display text-xl tracking-wide">1976</dd>
              </div>
              <div>
                <dt className="eyebrow">Accolades</dt>
                <dd className="mt-2 font-display text-xl tracking-wide">JIIA · Architect of the Year</dd>
              </div>
            </dl>
          </Reveal>
        </div>
      </section>

      {/* Nishant */}
      <section className="border-t border-mist">
        <div className="container-page py-24 md:py-40 grid grid-cols-1 md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-4">
            <RevealImage aspect="aspect-[4/5]" className="mb-8">
              <img
                src="/images/owner/Nishant_Machhar.jpg"
                alt="Nishant Machhar — Principal Architect"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </RevealImage>
            <div className="eyebrow mb-4">Principal Architect</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-6xl tracking-normal sm:tracking-widest2 leading-tight">
              Nishant<br />Machhar
            </h2>
          </Reveal>
          <Reveal className="md:col-span-8" delay={0.1}>
            <div className="space-y-6 text-base md:text-lg leading-relaxed">
              <p>
                Born and brought up in Bhavnagar, Gujarat. Post schooling, he attended the Institute
                of Environmental Design (IED) at Vallabh Vidyanagar and graduated with a Bachelors
                Degree in Architecture in 2004. Following two years of work experience, he moved to
                Cincinnati, Ohio, USA in 2006 to pursue a Master of Architecture at the School of
                Architecture &amp; Interior Design — DAAP, University of Cincinnati.
              </p>
              <p className="text-smoke">
                During his studies, he interned with <strong className="text-ink font-normal">Perkins+Will</strong> at their Atlanta office,
                working on housing and healthcare projects — graduating with his Master of
                Architecture at the end of 2008.
              </p>
              <p className="text-smoke">
                He moved back to India in December 2008 and worked with <strong className="text-ink font-normal">HCP Design &amp; Project
                Management Pvt. Ltd.</strong> at Ahmedabad, contributing to housing, institutional, and
                science &amp; technology projects. He started his own practice in 2011 across
                Ahmedabad and Bhavnagar, eventually moving to Bhavnagar in 2014 to lead his
                father's practice as a Principal Architect.
              </p>
              <p className="text-smoke">
                Nishant is a regular external juror at APIED (Vallabh Vidyanagar), IPSA (Rajkot) and
                the School of Architecture at CEPT University for the Master of Design program. He
                has mentored interns and chaired dissertation theses for many students. Nishant
                also has a keen interest in transportation design and mobility — writing and
                researching on the subject.
              </p>
            </div>

            <dl className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-6 border-t border-mist pt-8">
              <div>
                <dt className="eyebrow">Education</dt>
                <dd className="mt-2 font-display text-xl tracking-wide">M. Arch · Univ. of Cincinnati · 2008</dd>
              </div>
              <div>
                <dt className="eyebrow">Internship</dt>
                <dd className="mt-2 font-display text-xl tracking-wide">Perkins+Will · Atlanta</dd>
              </div>
              <div>
                <dt className="eyebrow">Academia</dt>
                <dd className="mt-2 font-display text-xl tracking-wide">APIED · IPSA · CEPT</dd>
              </div>
            </dl>
          </Reveal>
        </div>
      </section>
    </>
  );
}
